import { Box, Card, Tab, Tabs } from '@mui/material';
import { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import IVRDialog from './components/IVRDialog';
import IVRList from './components/IVRList';
import Page from '../../components/util/Page';
import { getCampaignsSummary } from '../../redux/slices/campaign';
import { deleteIvrMaps, getIvrMaps } from '../../redux/slices/organization';
import { RootState, useSelector } from '../../redux/store';
import { getIVRProviders } from 'redux/slices/ivr';
import AreYouSureModal from 'components/util/AreYouSureModal';
import TestIvrDialog from './components/TestIvrDialog';
import useCustomDispatch from 'redux/dispatch';
import { Button } from '@nps.today/component-library';
import Icon from 'components/icons/Icon';
import IVRSettings from './components/IVRSettings';
import { useNavigate, useParams } from 'react-router';
import { PATH_DASHBOARD } from 'routes/paths';
import { useTranslator } from 'translation/useTranslator';

export enum IVRTab {
  CONNECTIONS = 'connections',
  SETTINGS = 'settings'
}

export default function IVR() {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const { ivrMaps, loading } = useSelector((state: RootState) => state.organization);
  const { campaignsSummary } = useSelector((state: RootState) => state.campaign);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>()
  });
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [ivrIdToEdit, setIvrIdToEdit] = useState<number | null>(null);

  const { tab = IVRTab.CONNECTIONS } = useParams();
  const navigate = useNavigate();

  const onTabChange = (newTab: IVRTab) => {
    navigate(PATH_DASHBOARD.ivr.tab.replace(':tab', newTab));
  };

  const TABS = [
    {
      value: IVRTab.CONNECTIONS,
      label: translator.connections(),
      icon: <Icon type="connection" />,
      component: (
        <Card sx={{ p: 3 }}>
          <IVRList
            ivrData={ivrMaps}
            loading={loading.ivr}
            selectionModel={selectionModel}
            setSelectionModel={setSelectionModel}
            setIvrIdToEdit={setIvrIdToEdit}
          />
        </Card>
      )
    },
    {
      value: IVRTab.SETTINGS,
      label: translator.settings(),
      icon: <Icon type="settings" />,
      component: <IVRSettings />
    }
  ];

  const selectedTab = TABS.find((aTab) => aTab.value === tab);

  useEffect(() => {
    customDispatch({ action: getIvrMaps, disableSuccessMessage: true });
    customDispatch({ action: getCampaignsSummary, disableSuccessMessage: true });
    customDispatch({ action: getIVRProviders, disableSuccessMessage: true });
  }, [customDispatch]);

  const [displayAreYouSureModal, setDisplayAreYouSureModal] = useState(false);
  const selectedIvrIds = Array.from(selectionModel.ids ?? []).map(Number);
  const idsToDelete = ivrIdToEdit === null ? selectedIvrIds : [ivrIdToEdit];

  const onDeleteIvrMaps = async () => {
    customDispatch({
      action: deleteIvrMaps,
      actionParameters: idsToDelete,
      onSuccess: () => {
        setDisplayAreYouSureModal(false);
        setIvrIdToEdit(null);
        customDispatch({ action: getIvrMaps, disableSuccessMessage: true });
      }
    });
  };

  return (
    <Page
      title={translator.ivr()}
      stickyHeaderContent={[
        [
          selectedTab?.value === IVRTab.CONNECTIONS && selectedIvrIds.length >= 1 ? (
            <div key="deleteIvr">
              <Button
                dataTestId="delete-ivrs"
                variant={'contained'}
                color={'error'}
                onClick={() => setDisplayAreYouSureModal(true)}
              >
                {translator.deleteIvrs(`${selectedIvrIds.length}`)}
              </Button>
            </div>
          ) : undefined,
          selectedTab?.value === IVRTab.CONNECTIONS && (
            <Button
              dataTestId="add-ivr"
              key="addIvr"
              variant={'contained'}
              onClick={() => setOpenAddDialog(true)}
            >
              {translator.addIvr()}
            </Button>
          )
        ]
      ]}
    >
      {!loading.getIvrMaps && (
        <>
          <div style={{ display: 'inline-grid' }}>
            <Tabs
              value={selectedTab?.value}
              scrollButtons="auto"
              variant="scrollable"
              allowScrollButtonsMobile
              onChange={(_, value) => onTabChange(value as IVRTab)}
            >
              {TABS.map((tab) => (
                <Tab
                  disableRipple
                  key={tab.value}
                  label={tab.label}
                  icon={tab.icon}
                  value={tab.value}
                />
              ))}
            </Tabs>
          </div>
          <Box sx={{ mb: 1 }} />
          {TABS.map((tab) => {
            const isMatched = tab.value === selectedTab?.value;
            return isMatched && <Box key={tab.value}>{tab.component}</Box>;
          })}
        </>
      )}
      <IVRDialog
        ivrIdToEdit={ivrIdToEdit}
        campaigns={campaignsSummary}
        open={ivrIdToEdit !== null || openAddDialog}
        onClose={() => {
          setIvrIdToEdit(null);
          setOpenAddDialog(false);
        }}
      />
      <AreYouSureModal
        type="Delete"
        display={displayAreYouSureModal}
        hideModal={() => setDisplayAreYouSureModal(false)}
        title={translator.deleteIvrConnections(`${idsToDelete?.length}`)}
        description={translator.deleteIvrConnectionsDescription(`${idsToDelete?.length}`)}
        onContinue={onDeleteIvrMaps}
      />
      <TestIvrDialog />
    </Page>
  );
}
